import * as Radix from "@radix-ui/themes";
import * as SentryRemix from "@sentry/remix";
import { never, Vivid } from "@vendrinc/frontend-ts";
import classnames from "classnames";
import * as Downshift from "downshift";
import React from "react";
import styles from "./Search.module.css";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import * as Algolia from "~/hooks/useAlgolia";
import * as Analytics from "~/hooks/useAnalytics";
import * as paths from "~/paths";

const { Icon } = Vivid;

export const Variant = {
  header: "header",
  header2: "header2",
  home: "home",
  home2: "home2",
  marketplace: "marketplace",
} as const;

export type Variant = (typeof Variant)[keyof typeof Variant];

export type Suggestion = {
  hitIndex: number;
  slug: string;
  label: string;
  logo: string | undefined;
  formatted: string;
  queryId: string | undefined;
  objectId: string;
  type: ObjectType;
};

type ObjectType =
  | {
      type: typeof Algolia.ObjectType.Product;
      companyId: string;
      companyName: string;
    }
  | { type: typeof Algolia.ObjectType.Company };

type Props = {
  variant: Variant;
  placeholder: string;
};

export const parseSuggestions = (
  currentInputValue: string,
  response: Algolia.AlgoliaSearchResult[],
  options: { hideDuplicate: boolean },
): Suggestion[] => {
  // If there is already a "Company" record
  // If there is a "Product" record that has the same name as the "Company" record
  // And the Company record is present in the search results,
  // Then remove the "Product" record from the search results because it's a duplicate
  const companiesPresent = new Set<string>();

  for (const hit of response) {
    if (hit.objectType === Algolia.ObjectType.Company) {
      companiesPresent.add(hit.name);
    }
  }

  return response
    .filter((hit) => {
      if (options.hideDuplicate) {
        return !(
          hit.objectType === Algolia.ObjectType.Product &&
          companiesPresent.has(hit.name) &&
          hit.name === hit.parentName
        );
      }
      return true;
    })
    .map((hit) => {
      // remove bolding from fuzzy search results
      // i.e. currentInputValue = freehand, hit.name = Freelance
      if (
        !hit.name.toLowerCase().includes(currentInputValue.trim().toLowerCase())
      ) {
        hit._highlightResult.name.value = hit.name;
      }

      return {
        hitIndex: hit.hitIndex,
        logo: hit.logo === "/images/supplier.svg" ? undefined : hit.logo,
        label: !options.hideDuplicate
          ? `${hit.name} (${hit.objectType})`
          : hit.name,
        slug: hit.companySlug,
        formatted: !options.hideDuplicate
          ? `${hit._highlightResult.name.value} (${hit.objectType})`
          : hit._highlightResult.name.value,
        productId: hit.objectID,
        queryId: hit.queryID,
        objectId: hit.objectID,
        type: (() => {
          switch (hit.objectType) {
            case Algolia.ObjectType.Product:
              return {
                type: hit.objectType,
                companyId: hit.parentId!,
                companyName: hit.parentName!,
              };

            case Algolia.ObjectType.Company:
              return {
                type: hit.objectType,
              };

            case Algolia.ObjectType.Sku:
              // Shouldn't get here for this Default index since Skus aren't returned but handling for Typescript
              return {
                type: Algolia.ObjectType.Company,
                companyId: hit.parentId!,
                companyName: hit.parentName!,
              };

            default:
              return never(hit.objectType);
          }
        })(),
      };
    });
};

export const Input = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const algolia = Algolia.useAlgolia({
    searchResultType: Algolia.AlgoliaSearchResultType.Default,
    hitsPerPage: 5,
  });

  const inputElementRef = React.useRef<HTMLInputElement>(null);

  // Suggestions
  const [items, setItems] = React.useState([] as Suggestion[]);

  const [hasSentSearchStartedEvent, setHasSentSearchStartedEvent] =
    React.useState(false);

  const { navigate } = React.useContext(ReactRouterStubsContext);

  const { trackButton, track } = Analytics.useAnalytics();

  const {
    inputValue,
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    getLabelProps,
    reset,
    setHighlightedIndex,
  } = Downshift.useCombobox({
    onInputValueChange({ inputValue: currentInputValue }) {
      if (currentInputValue.trim().length <= 0) {
        setItems([]);
      } else {
        if (!hasSentSearchStartedEvent) {
          track({
            type: "Search Started",
            properties: {
              search_type: "product",
              search_query: currentInputValue,
            },
          });

          setHasSentSearchStartedEvent(true);
        }

        algolia
          .search(currentInputValue)
          .then((response) => {
            setItems(
              parseSuggestions(currentInputValue, response, {
                hideDuplicate: true,
              }),
            );
            setHighlightedIndex(0);
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
    items,
    itemToString(suggestion: Suggestion | null) {
      return (suggestion && suggestion.label) || "";
    },
    onSelectedItemChange(changes) {
      const item = changes.selectedItem;

      if (item) {
        try {
          inputElementRef.current?.blur();
          const { productId, companyId, companyName, productName } = (() => {
            switch (item.type.type) {
              case Algolia.ObjectType.Product:
                return {
                  productId: item.objectId,
                  productName: item.label,
                  companyId: item.type.companyId,
                  companyName: item.type.companyName,
                };

              case Algolia.ObjectType.Company:
                return {
                  productId: undefined,
                  productName: undefined,
                  companyId: item.objectId,
                  companyName: item.label,
                };
              default:
                return never(item.type);
            }
          })();

          track({
            type: "Search Item Selected",
            properties: {
              product_id: productId,
              product_name: productName,
              supplier_id: companyId,
              supplier_name: companyName,
              selection_method:
                changes.type === "__input_keydown_enter__" ? "return" : "click",
              selection_type: "product",
              search_type: "product",
              selection_position: item.hitIndex,
              index: algolia.indexName,
              queryID: item.queryId,
              objectID: item.objectId,
            },
          });

          navigate(paths.company(item.slug, productId));
        } catch (e) {
          SentryRemix.addBreadcrumb({
            level: "error",
            data: e as Record<string, unknown>,
            message: `Navigate threw an exception! ${String(e)}`,
          });
          if (e instanceof DOMException && e.name === "AbortError") {
            // User aborted navigation, ignoring.
          } else {
            throw e;
          }
        }
      }
    },
  });

  const { htmlFor: labelHtmlFor, ...otherLabelProps } = getLabelProps();

  return (
    <Radix.Box ref={ref} className={variantStyle[props.variant].container}>
      <Radix.Flex
        gap="2"
        align="center"
        className={classnames(styles.input, variantStyle[props.variant].input)}
      >
        <Radix.VisuallyHidden>
          <label htmlFor={labelHtmlFor} {...otherLabelProps}>
            Search marketplace
          </label>
        </Radix.VisuallyHidden>
        {props.variant !== Variant.home && (
          <Radix.Text color="gray" style={{ width: "26px" }}>
            {algolia.isRequestActive ? <Radix.Spinner /> : Icon.search}
          </Radix.Text>
        )}
        <input
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck="false"
          placeholder={props.placeholder}
          {...getInputProps({
            onFocus: () => {
              inputElementRef.current?.select();

              setHasSentSearchStartedEvent(false);

              track({
                type: "Search Focused",
                properties: {
                  search_type: "product",
                  focus_method: "click", // e.type always returns 'focus', so not sure what should go here?
                },
              });
            },
            ref: inputElementRef,
          })}
        />

        {!isHomeVariant(props.variant) && (
          <Radix.Flex display={inputValue.trim() !== "" ? "flex" : "none"}>
            <Radix.IconButton
              onClick={() => {
                trackButton({
                  element_text: "Reset search input",
                  element_location: "Search",
                });
                reset();
                inputElementRef.current?.focus();
              }}
              variant="ghost"
              size="1"
            >
              <Radix.Text color="gray">{Icon.close}</Radix.Text>
            </Radix.IconButton>
          </Radix.Flex>
        )}
        {props.variant === Variant.home && (
          <Radix.Button
            loading={algolia.isRequestActive}
            size="3"
            onClick={() => {
              trackButton({
                // NOTE: This button only focusses search input and
                // never performs a search action
                element_text: "Search",
                element_location: "Search",
              });
              inputElementRef.current?.focus();
            }}
          >
            Search
          </Radix.Button>
        )}
      </Radix.Flex>
      <Radix.Flex
        p="2"
        display={isOpen && items.length ? "flex" : "none"}
        mt="1"
        className={classnames(
          styles.suggestions,
          variantStyle[props.variant].suggestions,
        )}
        direction="column"
        {...getMenuProps()}
      >
        {items.map((item, index) => (
          <Radix.Box
            className={styles.suggestion}
            key={index}
            {...getItemProps({ item, index })}
          >
            <Radix.Flex gap="3" align="center" pl="1" pr="3" justify="between">
              <Radix.Flex
                gap="3"
                align="center"
                p="1"
                style={{ width: "100%" }}
              >
                <Radix.Avatar
                  src={item.logo}
                  size={props.variant === "header" ? "2" : "3"}
                  color="gray"
                  fallback={item.label[0]}
                ></Radix.Avatar>
                <Radix.Text
                  className={styles.suggestionText}
                  key={index}
                  style={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                  size="3"
                  color="gray"
                  highContrast
                  dangerouslySetInnerHTML={{ __html: item.formatted }}
                ></Radix.Text>
              </Radix.Flex>
            </Radix.Flex>
          </Radix.Box>
        ))}
      </Radix.Flex>
    </Radix.Box>
  );
});

Input.displayName = "SearchInput";

type VariantStyle = { input: string; container: string; suggestions: string };

const variantStyle: Record<Variant, VariantStyle> = {
  header: {
    input: styles.inputHeader,
    container: styles.containerHeader,
    suggestions: styles.suggestionsHeader,
  },
  header2: {
    input: styles.inputHeader2,
    container: styles.containerHeader2,
    suggestions: styles.suggestionsHeader,
  },
  home: {
    input: styles.inputHome,
    container: styles.containerHome,
    suggestions: styles.suggestionsHome,
  },
  home2: {
    input: styles.inputHome2,
    container: styles.containerHome2,
    suggestions: styles.suggestionsHome,
  },
  marketplace: {
    input: styles.inputMarketplace,
    container: styles.containerMarketplace,
    suggestions: styles.suggestionsMarketplace,
  },
};

const isHomeVariant = (variant: Variant) => {
  switch (variant) {
    case Variant.home:
    case Variant.home2:
      return true;
    case Variant.header:
    case Variant.marketplace:
    case Variant.header2:
      return false;
    default:
      return never(variant);
  }
};
